/**
 * Seleccion de productos que se combinan.
 *
 * Cada producto puede llevar varias filas: una talla y una cantidad por fila, para
 * poder pedir una M y una L del mismo articulo. Los indices de items[] se reparten
 * al enviar, en orden, porque anadir o quitar filas dejaria huecos.
 *
 * El descuento no se calcula aqui: Shopify lo aplica por cantidad sobre todo el
 * carrito, y adivinarlo daria un numero distinto al del checkout.
 *
 * Sin JavaScript el formulario no envia nada util, asi que el boton se habilita
 * solo cuando el componente arranca.
 */
class WuffyBundle extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.checks = Array.from(this.querySelectorAll('[data-wuffy-bundle-item]'));
    this.totalNode = this.querySelector('[data-wuffy-bundle-total]');
    this.countNode = this.querySelector('[data-wuffy-bundle-count]');
    this.addButton = this.querySelector('[data-wuffy-bundle-add]');

    if (!this.checks.length) return;

    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.form?.addEventListener('submit', this.#onSubmit);
    this.#render();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  #onChange = () => this.#render();

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const add = target.closest('[data-wuffy-add-row]');
    if (add instanceof HTMLElement) {
      event.preventDefault();
      this.#addRow(add.dataset.wuffyAddRow);
      return;
    }

    const drop = target.closest('[data-wuffy-drop-row]');
    if (drop instanceof HTMLElement) {
      event.preventDefault();
      const row = drop.closest('[data-wuffy-row]');
      const group = row?.parentElement;
      if (row && group && group.querySelectorAll('[data-wuffy-row]').length > 1) {
        row.remove();
        this.#refreshDropButtons(group);
        this.#render();
      }
    }
  };

  /** @param {string | undefined} index */
  #addRow(index) {
    const group = this.querySelector(`[data-wuffy-rows="${index}"]`);
    if (!group) return;
    const rows = group.querySelectorAll('[data-wuffy-row]');
    const copy = rows[rows.length - 1].cloneNode(true);
    if (!(copy instanceof HTMLElement)) return;

    const qty = copy.querySelector('[data-wuffy-qty]');
    if (qty instanceof HTMLInputElement) qty.value = '1';

    // La fila nueva no puede repetir una talla ya elegida: el comprador acabaria
    // pidiendo dos veces lo mismo creyendo que pidio dos tallas distintas.
    const nuevoSelect = copy.querySelector('select[data-wuffy-variant]');
    if (nuevoSelect instanceof HTMLSelectElement) {
      const usadas = new Set(
        Array.from(group.querySelectorAll('select[data-wuffy-variant]')).map((s) => s.value)
      );
      const libre = Array.from(nuevoSelect.options).find((o) => !o.disabled && !usadas.has(o.value));
      if (!libre) return; // no quedan tallas: no se anade una fila repetida
      nuevoSelect.value = libre.value;
    }

    group.appendChild(copy);
    this.#refreshDropButtons(group);
    this.#render();
  }

  /** La primera fila no se puede quitar: dejaria el producto sin talla. */
  #refreshDropButtons(group) {
    const rows = group.querySelectorAll('[data-wuffy-row]');
    rows.forEach((row, i) => {
      const drop = row.querySelector('[data-wuffy-drop-row]');
      if (drop instanceof HTMLElement) drop.hidden = rows.length < 2 || i === 0;
    });
  }

  /**
   * Reparte items[0..n] sobre las filas vivas. Un items[1] sin items[0] llega al
   * carrito como un hueco y Shopify lo rechaza.
   */
  #onSubmit = () => {
    let slot = 0;
    for (const check of this.checks) {
      const group = this.querySelector(`[data-wuffy-rows="${check.dataset.wuffyBundleItem}"]`);
      if (!group) continue;

      for (const row of group.querySelectorAll('[data-wuffy-row]')) {
        const variante = row.querySelector('[data-wuffy-variant]');
        const qty = row.querySelector('[data-wuffy-qty]');
        if (!(variante instanceof HTMLSelectElement || variante instanceof HTMLInputElement)) continue;
        if (!(qty instanceof HTMLInputElement)) continue;

        if (check.checked) {
          variante.disabled = false;
          qty.disabled = false;
          variante.name = `items[${slot}][id]`;
          qty.name = `items[${slot}][quantity]`;
          slot += 1;
        } else {
          variante.disabled = true;
          qty.disabled = true;
        }
      }
    }
  };

  #render() {
    let cents = 0;
    let marcados = 0;

    for (const check of this.checks) {
      if (!check.checked) continue;
      marcados += 1;
      const group = this.querySelector(`[data-wuffy-rows="${check.dataset.wuffyBundleItem}"]`);
      if (!group) continue;

      for (const row of group.querySelectorAll('[data-wuffy-row]')) {
        cents += this.#precioFila(row) * this.#cantidadFila(row);
      }
    }

    if (this.totalNode) this.totalNode.textContent = this.#money(cents);
    if (this.countNode) {
      this.countNode.textContent = marcados
        ? `${marcados} of ${this.checks.length} selected`
        : 'Nothing selected yet';
    }
    if (this.addButton instanceof HTMLButtonElement) this.addButton.disabled = marcados === 0;
    this.#syncDisabled();
  }

  /**
   * Los controles de un articulo sin marcar quedan fuera: ni se envian ni se
   * pueden enfocar con el teclado. El CSS ya los esconde, pero esconder algo
   * enfocable deja al que navega con tabulador dentro de una caja invisible.
   */
  #syncDisabled() {
    for (const check of this.checks) {
      const group = this.querySelector(`[data-wuffy-rows="${check.dataset.wuffyBundleItem}"]`);
      if (!group) continue;
      const apagado = !check.checked;
      group.querySelectorAll('[data-wuffy-variant], [data-wuffy-qty], [data-wuffy-drop-row]').forEach((el) => {
        if (el instanceof HTMLElement && 'disabled' in el) el.disabled = apagado;
      });
      const anadir = this.querySelector(`[data-wuffy-add-row="${check.dataset.wuffyBundleItem}"]`);
      if (anadir instanceof HTMLButtonElement) anadir.disabled = apagado;
    }
  }

  #precioFila(row) {
    const v = row.querySelector('[data-wuffy-variant]');
    if (v instanceof HTMLSelectElement) return Number(v.selectedOptions[0]?.dataset.price) || 0;
    if (v instanceof HTMLInputElement) return Number(v.dataset.price) || 0;
    return 0;
  }

  #cantidadFila(row) {
    const q = row.querySelector('[data-wuffy-qty]');
    return q instanceof HTMLInputElement ? Math.max(1, Number(q.value) || 1) : 1;
  }

  /** @param {number} cents */
  #money(cents) {
    const currency = this.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }
}

if (!customElements.get('wuffy-bundle')) {
  customElements.define('wuffy-bundle', WuffyBundle);
}
